import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

/**
 * Sentry configuration and initialization for Vue 3
 * Only enabled in production environment
 */

interface SentryConfig {
  app: App
  router?: Router
}

// Sentry DSN
const SENTRY_DSN = 'https://27112632ee43cb9f2fc541429c549a68@o4510657699184640.ingest.us.sentry.io/4510657700691968'

/**
 * Initialize Sentry for a Vue 3 application
 * Call this before mounting the app
 */
export const initSentry = ({ app, router }: SentryConfig): void => {
  // Only enable in production
  const env = process.env.NODE_ENV || 'development'
  if (env !== 'production') {
    return
  }

  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: env,

    // Performance monitoring
    integrations: router ? [
      Sentry.browserTracingIntegration({ router }),
    ] : [],

    // Sample 20% of transactions for performance monitoring
    tracesSampleRate: 0.2,

    // Capture Replay for 10% of all sessions
    replaysSessionSampleRate: 0.1,

    // Capture Replay for 100% of sessions with an error
    replaysOnErrorSampleRate: 1.0,

    // Filter out common noise
    ignoreErrors: [
      'Network Error',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      'ChunkLoadError',
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Navigation cancelled',
      'NavigationDuplicated',
    ],

    // Filter out browser extension errors
    beforeSend(event) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
      const isFromExtension = frames.some((frame: any) =>
        frame.filename?.includes('chrome-extension://') ||
        frame.filename?.includes('moz-extension://')
      )
      if (isFromExtension) {
        return null
      }
      return event
    },
  })
}

/**
 * Capture a custom error
 */
export const captureError = (error: Error): void => {
  Sentry.captureException(error)
}

export default {
  initSentry,
  captureError,
}
